import React, { useState } from "react";
import { RLineChart, RBarChart } from "../../../ChartUtility/recharts";
import { scopeColorAt } from "./chartPalette";
import { EMISSION_UNIT, formatTonnes, toTonnesRows } from "./emissionUnits";
import useScopeSummary from "../../common/hooks/useEmission";
import { PeriodCompare } from "./PeriodCompare";
import { downloadComparisonExcel, downloadBtnStyle } from "./scopeExcel";
import * as S from "./scopeDashboard.styles";

const G1_COLOR = scopeColorAt(0);
const G2_COLOR = scopeColorAt(2);

const FY = "fy";
const LOCATION = "location";
const KPI = "kpi";
const PERIOD = "period";
const BAR = "bar";
const LINE = "line";

/** Sum a scope's category values per month → [{ month, value }]. */
const monthlyTotals = (monthly = [], catKeys = []) =>
  monthly.map((row) => ({
    month: row.month,
    value: catKeys.reduce((s, k) => s + Number(row[k] || 0), 0),
  }));

/** Merge two [{month,value}] series into overlaid rows { month, g1, g2 }. */
const mergeTrend = (m1 = [], m2 = []) => {
  const map = new Map();
  m1.forEach((r) => map.set(r.month, { month: r.month, g1: r.value, g2: 0 }));
  m2.forEach((r) => {
    const e = map.get(r.month) || { month: r.month, g1: 0, g2: 0 };
    e.g2 = r.value;
    map.set(r.month, e);
  });
  return [...map.values()];
};

const fyLabel = (fys, id) => {
  const f = (fys || []).find((x) => String(x.id) === String(id));
  return f ? (f.financial_year_value || f.name) : "";
};
const locLabel = (locs, id) => (locs || []).find((l) => String(l.id) === String(id))?.name || "";

/**
 * Overlaid comparison of a scope's emissions between two groups (ESG-style).
 * The compared dimension (FY / Location / KPI / Period) splits into Group 1 vs
 * Group 2; the others become shared COMMON filters. Nothing is pre-selected,
 * Group 2 can never repeat Group 1, and a compare-by dimension is only offered
 * when the data holds two or more of it. Totals are shown as tonnes with a
 * difference card, and the trend can switch between bars and a line.
 * @param {string} props.scopeKey - API scope key, e.g. "scope 1"
 * @param {object} props.selectedFY - default FY object
 * @param {object[]} props.financialYears
 * @param {{id:number,name:string}[]} props.locations
 */
export const ComparisonView = ({ scopeKey, selectedFY, financialYears = [], locations = [] }) => {
  const baseFy = selectedFY?.id;
  const [compareByRaw, setCompareBy] = useState(null);
  const [trendChart, setTrendChart] = useState(BAR);
  const [commonFy, setCommonFy] = useState("");
  const [commonLoc, setCommonLoc] = useState("");
  const [g1Fy, setG1Fy] = useState("");
  const [g2Fy, setG2Fy] = useState("");
  const [g1Loc, setG1Loc] = useState("");
  const [g2Loc, setG2Loc] = useState("");
  const [g1Kpi, setG1Kpi] = useState("");
  const [g2Kpi, setG2Kpi] = useState("");

  const effCommonFy = commonFy || baseFy;
  const commonLocArr = commonLoc ? [Number(commonLoc)] : [];

  // Base fetch (common context) — drives KPI options + KPI-mode data + period availability.
  const sBase = useScopeSummary(scopeKey, effCommonFy, commonLocArr);
  const pBase = sBase.scopeData?.[scopeKey];
  const kpiOptions = pBase?.topKpis || [];

  const periodAvail = financialYears.length >= 2 || (pBase?.monthly?.length || 0) >= 2;
  const availableDims = [
    financialYears.length >= 2 && { key: FY, label: "Financial Year" },
    locations.length >= 2 && { key: LOCATION, label: "Location" },
    kpiOptions.length >= 2 && { key: KPI, label: "KPI" },
    periodAvail && { key: PERIOD, label: "Period" },
  ].filter(Boolean);
  const compareBy = availableDims.some((d) => d.key === compareByRaw)
    ? compareByRaw
    : (availableDims[0]?.key || null);

  // Group fetches (FY / Location comparison need two live fetches).
  const fy1 = compareBy === FY ? g1Fy : effCommonFy;
  const fy2 = compareBy === FY ? g2Fy : effCommonFy;
  const loc1 = compareBy === LOCATION ? (g1Loc ? [Number(g1Loc)] : []) : commonLocArr;
  const loc2 = compareBy === LOCATION ? (g2Loc ? [Number(g2Loc)] : []) : commonLocArr;
  const s1 = useScopeSummary(scopeKey, fy1 || undefined, loc1);
  const s2 = useScopeSummary(scopeKey, fy2 || undefined, loc2);
  const p1 = s1.scopeData?.[scopeKey];
  const p2 = s2.scopeData?.[scopeKey];

  const fyOptions = financialYears.map((f) => ({ value: f.id, label: f.financial_year_value || f.name }));
  const locOptions = locations.map((l) => ({ value: l.id, label: l.name }));
  const kpiSelectOptions = kpiOptions.map((k) => ({ value: k.questionId, label: k.title || `KPI ${k.questionId}` }));

  // ── Both groups picked and different? ──
  const groupSel = {
    [FY]: [g1Fy, g2Fy],
    [LOCATION]: [g1Loc, g2Loc],
    [KPI]: [g1Kpi, g2Kpi],
  }[compareBy] || ["", ""];
  const ready = Boolean(groupSel[0]) && Boolean(groupSel[1]) && String(groupSel[0]) !== String(groupSel[1]);

  // ── Normalise each group ──
  let n1 = { total: 0, monthly: [], categories: {}, label: "Group 1" };
  let n2 = { total: 0, monthly: [], categories: {}, label: "Group 2" };
  const showCategories = compareBy !== KPI;

  if (ready && compareBy === KPI) {
    const kA = String(g1Kpi);
    const kB = String(g2Kpi);
    const metaA = kpiOptions.find((k) => String(k.questionId) === kA);
    const metaB = kpiOptions.find((k) => String(k.questionId) === kB);
    const rows = pBase?.monthlyByKpi || [];
    n1 = { total: metaA?.value || 0, monthly: rows.map((r) => ({ month: r.month, value: Number(r[kA] || 0) })), categories: {}, label: metaA?.title || "KPI 1" };
    n2 = { total: metaB?.value || 0, monthly: rows.map((r) => ({ month: r.month, value: Number(r[kB] || 0) })), categories: {}, label: metaB?.title || "KPI 2" };
  } else if (ready) {
    const c1 = Object.keys(p1?.categories || {});
    const c2 = Object.keys(p2?.categories || {});
    const label1 = compareBy === FY ? `FY ${fyLabel(financialYears, g1Fy)}` : (locLabel(locations, g1Loc) || "Group 1");
    const label2 = compareBy === FY ? `FY ${fyLabel(financialYears, g2Fy)}` : (locLabel(locations, g2Loc) || "Group 2");
    n1 = { total: p1?.total_emission || 0, monthly: monthlyTotals(p1?.monthly, c1), categories: p1?.categories || {}, label: label1 };
    n2 = { total: p2?.total_emission || 0, monthly: monthlyTotals(p2?.monthly, c2), categories: p2?.categories || {}, label: label2 };
  }

  const loading = ready && (compareBy === KPI ? sBase.loading : (s1.loading || s2.loading));

  const trendData = mergeTrend(n1.monthly, n2.monthly);
  const trendDataT = toTonnesRows(trendData);
  const trendSeries = [
    { key: "g1", name: n1.label, color: G1_COLOR, dot: true, maxBarSize: 46, getColor: () => G1_COLOR },
    { key: "g2", name: n2.label, color: G2_COLOR, dot: true, maxBarSize: 46, getColor: () => G2_COLOR },
  ];
  const singleBucket = trendData.length < 2;
  const asBars = trendChart === BAR || singleBucket;

  const catUnion = [...new Set([...Object.keys(n1.categories), ...Object.keys(n2.categories)])];
  const catData = catUnion.map((c) => ({ category: c, g1: Number(n1.categories[c] || 0), g2: Number(n2.categories[c] || 0) }));
  const catDataT = toTonnesRows(catData, "category");
  const catSeries = [
    { key: "g1", name: n1.label, color: G1_COLOR },
    { key: "g2", name: n2.label, color: G2_COLOR },
  ];

  const delta = n1.total > 0 ? ((n2.total - n1.total) / n1.total) * 100 : 0;
  const peak = Math.max(n1.total, n2.total);
  const legendItems = [
    { label: n1.label, color: G1_COLOR },
    { label: n2.label, color: G2_COLOR },
  ];
  const dimNoun = { [FY]: "financial years", [LOCATION]: "locations", [KPI]: "KPIs" }[compareBy] || "groups";

  const trendPanel = (
    <S.Panel>
      <S.PanelHead>
        <S.PanelTitles>
          <S.PanelTitle>Emissions trend</S.PanelTitle>
          <S.PanelSub>Monthly, {EMISSION_UNIT}</S.PanelSub>
        </S.PanelTitles>
        {!singleBucket && <ChartTypeToggle value={trendChart} onChange={setTrendChart} />}
      </S.PanelHead>
      <ChartLegend items={legendItems} />
      {asBars ? (
        <RBarChart data={trendDataT} xKey="month" series={trendSeries} height={280} unit={EMISSION_UNIT} animate />
      ) : (
        <RLineChart data={trendDataT} xKey="month" series={trendSeries} height={280} unit={EMISSION_UNIT} animate />
      )}
    </S.Panel>
  );

  const categoryPanel = (
    <S.Panel>
      <S.PanelHead>
        <S.PanelTitles>
          <S.PanelTitle>By category</S.PanelTitle>
          <S.PanelSub>{EMISSION_UNIT} per category</S.PanelSub>
        </S.PanelTitles>
      </S.PanelHead>
      <ChartLegend items={legendItems} />
      <RBarChart data={catDataT} xKey="category" series={catSeries} height={280} unit={EMISSION_UNIT} animate />
    </S.Panel>
  );

  const compareByToggle = availableDims.length > 0 && (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>Compare by:</span>
      <S.Toggle role="tablist" aria-label="Compare by">
        {availableDims.map((d) => (
          <S.ToggleBtn key={d.key} type="button" role="tab" aria-selected={compareBy === d.key} $active={compareBy === d.key} onClick={() => setCompareBy(d.key)}>
            {d.label}
          </S.ToggleBtn>
        ))}
      </S.Toggle>
    </div>
  );

  if (compareBy === PERIOD) {
    return (
      <S.Wrap>
        <PeriodCompare
          compareByToggle={compareByToggle}
          scopeKey={scopeKey}
          financialYears={financialYears}
          locations={locations}
          defaultFyId={baseFy}
        />
      </S.Wrap>
    );
  }

  return (
    <S.Wrap>
      <S.Panel>
        <S.PanelHead>
          <S.PanelTitles>
            <S.PanelTitle>Filters</S.PanelTitle>
            <S.PanelSub>Compare two groups against each other</S.PanelSub>
          </S.PanelTitles>
          {compareByToggle}
        </S.PanelHead>

        {availableDims.length === 0 ? (
          <div style={{ fontSize: 13, color: "#94a3b8", padding: "8px 0" }}>
            Not enough data to compare — need at least two financial years, locations, or KPIs.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 24 }}>
            {compareBy === FY ? (
              <GroupPair label="Financial Year"
                g1Value={g1Fy} onG1={setG1Fy} g2Value={g2Fy} onG2={setG2Fy}
                options={fyOptions} placeholder="Select FY…" />
            ) : (
              <CommonSelect label="Financial Year" value={effCommonFy || ""} onChange={setCommonFy} options={fyOptions} />
            )}

            {compareBy === LOCATION ? (
              <GroupPair label="Location"
                g1Value={g1Loc} onG1={setG1Loc} g2Value={g2Loc} onG2={setG2Loc}
                options={locOptions} placeholder="Select location…" />
            ) : (
              <CommonSelect label="Location" value={commonLoc} onChange={setCommonLoc} options={locOptions} placeholder="All Locations" />
            )}

            {compareBy === KPI && (
              <GroupPair label="KPI"
                g1Value={g1Kpi} onG1={setG1Kpi} g2Value={g2Kpi} onG2={setG2Kpi}
                options={kpiSelectOptions} placeholder="Select KPI…" />
            )}
          </div>
        )}
      </S.Panel>

      {availableDims.length === 0 ? null : !ready ? (
        <S.Panel>
          <div style={{ fontSize: 13, color: "#64748b", padding: "20px 0", textAlign: "center" }}>
            Pick two different {dimNoun} (Group 1 and Group 2) to see the comparison.
          </div>
        </S.Panel>
      ) : loading ? (
        <S.Skeleton aria-hidden="true" />
      ) : (
        <>
          <S.Panel>
            <S.PanelHead>
              <S.PanelTitles>
                <S.PanelTitle>Total emission</S.PanelTitle>
                <S.PanelSub>{EMISSION_UNIT} · Group 1 vs Group 2</S.PanelSub>
              </S.PanelTitles>
              <button
                type="button"
                style={downloadBtnStyle}
                onClick={() => downloadComparisonExcel({
                  scopeTitle: scopeKey,
                  compareByLabel: availableDims.find((d) => d.key === compareBy)?.label || compareBy,
                  g1: { label: n1.label, total: n1.total },
                  g2: { label: n2.label, total: n2.total },
                  trendData,
                  catData: showCategories ? catData : [],
                })}
              >
                ⬇ Download Excel
              </button>
            </S.PanelHead>
            <S.StatGrid>
              <TotalStat color={G1_COLOR} label={n1.label} value={n1.total} peak={peak} groupLabel="Group 1" />
              <TotalStat color={G2_COLOR} label={n2.label} value={n2.total} peak={peak} groupLabel="Group 2" />
              <DeltaStat
                delta={delta}
                diff={n2.total - n1.total}
                comparable={n1.total > 0}
                g1Label={n1.label}
                g2Label={n2.label}
              />
            </S.StatGrid>
          </S.Panel>

          {showCategories ? (
            <S.Grid>
              {trendPanel}
              {categoryPanel}
            </S.Grid>
          ) : (
            trendPanel
          )}
        </>
      )}
    </S.Wrap>
  );
};

const CommonBadge = () => (
  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: "#3f88a5", background: "#e8f4f8", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>
    COMMON
  </span>
);

/** Single shared selector for a non-compared dimension. */
const CommonSelect = ({ label, value, onChange, options = [], placeholder }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>{label}<CommonBadge /></span>
    <select className="kpi-inline-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

/**
 * Group 1 vs Group 2 selector pair. Nothing pre-selected (placeholder shown),
 * and each group's options exclude the other's pick so the two groups can never
 * be identical.
 */
const GroupPair = ({ label, g1Value, onG1, g2Value, onG2, options = [], placeholder }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>{label}</span>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <SubSelect color={G1_COLOR} groupLabel="Group 1" value={g1Value} onChange={onG1} placeholder={placeholder}
        options={options.filter((o) => String(o.value) !== String(g2Value))} />
      <span style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>vs</span>
      <SubSelect color={G2_COLOR} groupLabel="Group 2" value={g2Value} onChange={onG2} placeholder={placeholder}
        options={options.filter((o) => String(o.value) !== String(g1Value))} />
    </div>
  </div>
);

const SubSelect = ({ color, groupLabel, value, onChange, placeholder, options = [] }) => (
  <label style={{ display: "inline-flex", flexDirection: "column", gap: 3, minWidth: 180 }}>
    <span style={{ fontSize: 10, fontWeight: 700, color }}>{groupLabel}</span>
    <select className="kpi-inline-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

/** Bars / Line switch for the emissions-trend panel. */
const ChartTypeToggle = ({ value, onChange }) => (
  <S.Toggle role="tablist" aria-label="Trend chart type">
    {[[BAR, "Bars"], [LINE, "Line"]].map(([key, label]) => (
      <S.ToggleBtn key={key} type="button" role="tab" aria-selected={value === key} $active={value === key} onClick={() => onChange(key)}>
        {label}
      </S.ToggleBtn>
    ))}
  </S.Toggle>
);

/** Visible group legend for the comparison charts (swatch + label). */
const ChartLegend = ({ items = [] }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: 10 }}>
    {items.map((it) => (
      <span key={it.label} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#475569" }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: it.color, flexShrink: 0 }} />
        {it.label}
      </span>
    ))}
  </div>
);

/** One group's total as a colour-accented card with a share bar vs the larger group. */
const TotalStat = ({ color, label, value, peak, groupLabel }) => {
  const share = peak > 0 ? (Number(value) / peak) * 100 : 0;
  return (
    <S.StatCard $accent={color}>
      <S.StatLabel>
        <S.StatSwatch $color={color} />
        {label}
      </S.StatLabel>
      <S.StatValue>
        {formatTonnes(value)}
        <span>{EMISSION_UNIT}</span>
      </S.StatValue>
      <S.StatShare $pct={share} $color={color}>
        <span />
      </S.StatShare>
      <S.StatFoot>
        {groupLabel} · {share.toFixed(0)}% of the larger group
      </S.StatFoot>
    </S.StatCard>
  );
};

/** Difference card — absolute + percentage change of Group 2 vs Group 1. */
const DeltaStat = ({ delta, diff, comparable, g1Label, g2Label }) => {
  const up = diff > 0;
  const flat = diff === 0;
  const tone = flat ? "#64748b" : up ? "#b0574c" : "#4f9d69";
  const higher = up ? g2Label : g1Label;
  const lower = up ? g1Label : g2Label;
  return (
    <S.StatCard $accent={tone}>
      <S.StatLabel>Difference</S.StatLabel>
      <S.StatValue $tone={tone}>
        {comparable ? `${flat ? "" : up ? "▲ " : "▼ "}${Math.abs(delta).toFixed(1)}%` : "—"}
      </S.StatValue>
      <S.StatFoot>
        {flat ? (
          `${g1Label} and ${g2Label} are equal`
        ) : (
          <>
            {formatTonnes(Math.abs(diff))} {EMISSION_UNIT}
            <br />
            <strong>{higher}</strong> higher than {lower}
          </>
        )}
      </S.StatFoot>
    </S.StatCard>
  );
};

export default ComparisonView;
